import React from 'react';
import { cloneDeep } from 'lodash';
import { assign } from 'xstate';
import { IntlShape } from 'react-intl';

import { BaseComponent } from '../Component';
import { ErrorBoundary } from '../../util/ErrorBoundary';
import { MachineContext } from '../../MachineContext';
import { translateWithFallback } from '../../util/actionUtils';

import { UninitializedCharacter } from './types';
import { SceneCanvasContextProvider, ViewMode } from './SceneCanvasContext';
import { SceneCanvasStart } from './SceneCanvasStart';

export interface SceneCanvasComponent extends BaseComponent {
  type: 'sceneCanvas';
  props: SceneCanvasProps;
}

export interface SceneCanvasProps {
  initialCharacters: UninitializedCharacter[];
  isEcho?: boolean;
  buttonText: string;
  saveResultTo?: string;
}

// SceneCanvas
// This component is a stack of components. This one contains an error boundary
// and the ContextProvider. The SceneCanvasStart component is extracted here
// because we have to consume the provider context there, so we can't put that
// nested component into this one.
export const SceneCanvas: React.FC<SceneCanvasProps> = ({
  initialCharacters,
  isEcho = false,
  buttonText,
  saveResultTo = '',
}) => (
  <ErrorBoundary
    fallback={<p>Sorry, there was an error rendering the SceneCanvas.</p>}
  >
    <SceneCanvasContextProvider
      initialCharacters={initialCharacters}
      viewMode={isEcho ? ViewMode.READ_ONLY : ViewMode.EDIT}
    >
      <SceneCanvasStart
        buttonText={buttonText}
        saveResultTo={saveResultTo}
        isEcho={isEcho}
      />
    </SceneCanvasContextProvider>
  </ErrorBoundary>
);

export const renderSceneCanvasAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;
    const getValueFrom = payload.getValueFrom;

    const initialCharacters = getValueFrom
      ? context.userData[getValueFrom]
      : [];

    newContext.widgetData.push({
      type: 'sceneCanvas',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        initialCharacters: initialCharacters,
        buttonText: translateWithFallback(intl, payload.buttonText),
        isEcho: payload.isEcho,
        saveResultTo: payload.saveResultTo,
      },
    });

    return newContext;
  });
};
